type EnvRecord = Record<string, string>;

const captureEnv = (): EnvRecord => {
  const merged: EnvRecord = {};
  const assignKey = (key: string, value: unknown) => {
    if (value === undefined || value === null) return;
    merged[key] = typeof value === 'string' ? value : String(value);
  };

  // CRA (react-scripts) inlines direct references like `process.env.REACT_APP_X` at build
  // time, but `process` itself does not exist in the browser. These reads must therefore
  // not sit behind a `typeof process` check, or production builds lose every value.
  assignKey('NODE_ENV', process.env.NODE_ENV);
  assignKey('REACT_APP_API_BASE_URL', process.env.REACT_APP_API_BASE_URL);
  assignKey('REACT_APP_RECAPTCHA_SITE_KEY', process.env.REACT_APP_RECAPTCHA_SITE_KEY);
  assignKey('REACT_APP_APPCHECK_DEBUG_TOKEN', process.env.REACT_APP_APPCHECK_DEBUG_TOKEN);
  assignKey('REACT_APP_USE_EMULATORS', process.env.REACT_APP_USE_EMULATORS);
  assignKey('REACT_APP_LIVE_MIN_FOLLOWERS', process.env.REACT_APP_LIVE_MIN_FOLLOWERS);

  const assign = (source?: Record<string, unknown>) => {
    if (!source) return;
    Object.entries(source).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      merged[key] = typeof value === 'string' ? value : String(value);
    });
  };

  assign(typeof process !== 'undefined' ? (process as any).env : undefined);
  // Note: This is a CRA (react-scripts) app; `import.meta` is not supported.

  return merged;
};

const ENV = captureEnv();

const expandKeys = (key: string): string[] => {
  if (!key) return [];
  if (key.startsWith('REACT_APP_')) return [key, key.replace('REACT_APP_', 'VITE_')];
  if (key.startsWith('VITE_')) return [key, key.replace('VITE_', 'REACT_APP_')];
  return [key];
};

export const getEnv = (key: string, fallback?: string): string | undefined => {
  for (const candidate of expandKeys(key)) {
    const value = ENV[candidate];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return fallback;
};

export const getEnvFlag = (key: string, fallback = 'false'): boolean => {
  const raw = getEnv(key, fallback);
  if (!raw) return false;
  return ['1', 'true', 'yes', 'on'].includes(raw.toLowerCase());
};

export const getNodeEnv = (): string => getEnv('NODE_ENV', 'production')!;
export const isProdEnv = (): boolean => getNodeEnv().toLowerCase() === 'production';
export const isDevEnv = (): boolean => !isProdEnv();
